import {
	type InvestmentActions,
	type InvestmentStatus,
	type InvestmentSummary,
	IntegrationReferenceControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { useFunctionalAreas } from "$root/App/context";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LabelRounded } from "$root/components/LabelRounded/Index";
import { PageDownloadAndMoreActionsMenu } from "$root/components/PageDownloadAndMoreActionsMenu";
import { type PageHeaderProps, PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import ReviewCompositionErrors from "$root/components/ReviewCompositionErrors";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { spawnAccessDialog } from "$root/functional-areas/acl/AccessDialog";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import EntityStatus from "$root/functional-areas/acl/EntityStatus";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import {
	type CompositionInRetrievingDataResponse,
	getCompositionDataInRetrievingData,
} from "$root/functional-areas/instruments/repository";
import { usePortfolioEntityManagementActions } from "$root/functional-areas/portfolio/entity-management";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { SmallInvestmentSummary } from "$root/functional-areas/portfolio/SmallInvestmentSummary";
import { useUserValue } from "$root/functional-areas/user";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { type ContextContent, downloadContentDisposition, objMatchFn, seconds } from "$root/utils";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { Card } from "$root/widgets-architecture/layout/Card";
import WidgetsGrid, { type WidgetGridHandles } from "$root/widgets-architecture/layout/WidgetsGrid";
import { Banner, Icon, Text } from "@mdotm/shadcn/legacy/components";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { useQuery } from "@tanstack/react-query";
import { type RefObject, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ManualEditEntitiesEnum } from "../ManualCreation/common";
import type { InvestmentSummaryWithRequiredUuid } from "../PortfolioDetails/common";
import { buildCompositionExportConfig } from "$root/functional-areas/instruments/export";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import { navigateToReviewIfAnythingToReviewSync } from "$root/functional-areas/instruments/review";
import { ReviewEntity } from "../ReviewInstruments.const";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { usePortfolioStudioRedirect } from "../PortfoliosStudio/usePortfolioStudioRedirect";
import { Button, DropdownMenu, type DropdownTriggerProps } from "@mdotm/shadcn/mdotm/components";
import { asyncStateFrom } from "@mdotm/utils/promise";

function EditReferenceTrigger({
	children,
	state,
	...props
}: DropdownTriggerProps & { children?: React.ReactNode }): React.JSX.Element {
	return (
		<Button
			state={asyncStateFrom(state)}
			progressBar={false}
			data-qualifier="ReferenceDetails/EditDropdownMenu/Button"
			size="small"
			palette="primary"
			{...props}
		>
			<Icon icon="Edit" size={16} />
			&nbsp;
			{children}
		</Button>
	);
}

function ReferenceDetails(): React.JSX.Element {
	const { uuid } = useParams<{ uuid: string }>();
	const { push } = useTypedNavigation();
	const { t } = useTranslation();

	const referenceSummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(uuid),
		keepPreviousData: true,
	});

	useFunctionalAreas(
		() => ({
			areas: "reference",
			data: { uuid, name: referenceSummary.data?.name },
		}),
		[uuid, referenceSummary.data?.name],
	);

	const portfolioStudio = usePortfolioStudioRedirect();
	useQueryReactor(referenceSummary, {
		onError(error) {
			const { data } = (error as any)?.response ?? {};
			if (data?.message === "Accesso negato" || data?.code === 404) {
				spawnAccessDialog({
					onClick: (onClose) => {
						portfolioStudio.navigate();
						onClose();
					},
				});
				return;
			}

			platformToast({
				children: t("SOMETHING_WENT_WRONG"),
				severity: "error",
				icon: "Portfolio",
			});
			push("PortfoliosStudio", { status: "error", tab: PortfolioStudioTab.References });
		},
		onSuccess(data) {
			if (data?.status === "REVIEW") {
				navigateToReviewIfAnythingToReviewSync(uuid, ReviewEntity.TARGET_INVESTMENT, () =>
					push("ReviewInstruments", {
						uuid,
						entity: ReviewEntity.TARGET_INVESTMENT,
					}),
				);
			}
		},
	});

	useGroupEventBus("investment-update", () => referenceSummary.refetch().catch(noop), {
		filter: objMatchFn({ uuid }),
		timeout: seconds(2),
	});

	useGroupEventBus("investment-report-update", () => referenceSummary.refetch().catch(noop), {
		filter: objMatchFn({ uuid }),
		timeout: seconds(2),
	});

	// An import finishing is the one change to this reference that only the backend knows about, and
	// it has its own topic — `investment-update` never fires for it. Without this the composition an
	// import (UI- or bot-driven) just wrote stays hidden until the page is reloaded.
	useGroupEventBus("reference-import-update", () => referenceSummary.refetch().catch(noop), {
		filter: objMatchFn({ uuid }),
		timeout: seconds(2),
	});

	useEventBus("shared-entity", {
		filter: objMatchFn({ sharedEntityUuid: uuid }),
		listener: () => {
			referenceSummary.refetch().catch(noop);
		},
	});

	return (
		<ReactQueryWrapperBase query={referenceSummary}>
			{(summary) => <ReferenceSummaryInner summary={{ ...summary, uuid }} />}
		</ReactQueryWrapperBase>
	);
}

export default ReferenceDetails;

function ReferenceSummaryInner({ summary }: { summary: InvestmentSummaryWithRequiredUuid }) {
	const crumbs = useCrumbs();

	const context = useMemo<ContextContent<typeof PortfolioContext>>(
		() => ({
			enhanced: false,
			portfolio: summary,
			selectedBenchmark: summary.primaryBenchmarkIdentifier ?? null,
			isMarketScenarioEnhancable: false,
			currentTab: 0,
			workspaceUid: undefined, // TODO: add once References becomes a smart workspace, too
		}),
		[summary],
	);

	const gridHandleRef = useRef<WidgetGridHandles | null>(null);

	return (
		<PortfolioContext.Provider value={context}>
			<PageHeader
				severity={summary?.status ? severityByStatus[summary?.status] : undefined}
				crumbs={[
					...crumbs.portfolioStudio(PortfolioStudioTab.References, summary?.uuid),
					...crumbs.referenceName(summary),
				]}
				titleAction={<ReferenceTitleActions summary={summary} gridHandleRef={gridHandleRef} />}
				subTitle={<ReferenceSubtitle summary={summary} />}
			/>

			{summary.status === "RETRIEVING_DATA" ? (
				<ReactQueryWrapper
					queryFn={() => getCompositionDataInRetrievingData(summary.uuid, "TARGET_INVESTMENT")}
					queryKey={["referenceComposition", summary.uuid, summary?.status]}
				>
					{(response) => <ReviewReferenceInner summary={summary} {...response} />}
				</ReactQueryWrapper>
			) : (
				<>
					<div className="mb-5">
						<SmallInvestmentSummary enhanced={false} portfolio={summary} />
					</div>
					<WidgetsGrid gridName="PORTFOLIO_REFERENCE" handleRef={gridHandleRef} />
				</>
			)}
		</PortfolioContext.Provider>
	);
}

function ReferenceSubtitle({ summary }: { summary: InvestmentSummary }) {
	const { action, actualAction, status, modificationDate, richAcl, uuid, name, lastActionDate } = summary;

	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();

	return (
		<div className="flex justify-between">
			<div className="w-full flex items-center space-x-4 text-[#585D68] pr-2">
				<div className="flex items-center" data-qualifier="PortfolioDetails/PageHeader/Status" data-status={status}>
					<span className="mr-2 uppercase">{t("STATUS")}</span>
					<LabelRounded content={{ status, component: "" }} />
				</div>
				<div className="flex items-center space-x-2" data-qualifier="PortfolioDetails/PageHeader/Action">
					<p className="uppercase whitespace-nowrap">{t("PORTFOLIOS.PF_LAST_ACTION")} </p>
					<p className="font-bold uppercase whitespace-nowrap">
						{action && actualAction ? t(`PORTFOLIO_ACTION_STATUS.${actualAction}`) : "-"}
					</p>
					{modificationDate && <div className="whitespace-nowrap">({formatDate(lastActionDate)})</div>}
				</div>

				<EntityStatus accessControl={richAcl} entity="INVESTMENT" entityId={uuid} entityName={name} />
			</div>
		</div>
	);
}

function ReferenceTitleActions({
	summary,
	gridHandleRef,
}: {
	summary: InvestmentSummary;
	gridHandleRef: RefObject<WidgetGridHandles | null>;
}) {
	const { status, richAcl, uuid, benchmarkLabeled, reference } = summary;
	const integrationReferenceApi = useApiGen(IntegrationReferenceControllerApiFactory);

	const { t } = useTranslation();
	const user = useUserValue();
	const { push } = useTypedNavigation();
	const portfolioStudio = usePortfolioStudioRedirect();
	const entityManagementActions = usePortfolioEntityManagementActions(summary, {
		onDuplicate: () => portfolioStudio.navigate(),
		onDelete: () => portfolioStudio.navigate(),
	});

	const canEditComposition = useMemo(
		() =>
			status !== "ERROR" &&
			status !== "RETRIEVING_DATA" &&
			status !== "CALCULATING" &&
			status !== "REVIEW" &&
			aclByArea.portfolio.canEditComposition(user.id, [
				{ userId: user.id, permissions: richAcl?.currentUserPermissions },
			]),
		[status, user.id, richAcl?.currentUserPermissions],
	);

	return (
		<div className="flex items-center space-x-2">
			{canEditComposition && (
				<DropdownMenu
					Trigger={EditReferenceTrigger}
					extraTriggerProps={{ children: t("BUTTON.EDIT") }}
					actions={[
						{
							disabled: !canEditComposition,
							icon: "composition",
							label: "Edit composition",
							"data-qualifier": "ReferenceDetails/EditDropdownMenu/DropdownItem(EditComposition)",
							onClick: () =>
								push("Portfolios/ManualEdit", {
									uuid: uuid!,
									entity: ManualEditEntitiesEnum.Reference,
								}),
						},
						{
							icon: "Content-Copy",
							disabled: !canEditComposition,
							label: "Copy composition",
							"data-qualifier": "ReferenceDetails/EditDropdownMenu/DropdownItem(CopyComposition)",
							onClick: () =>
								push("Portfolios/ManualEdit", {
									uuid: uuid!,
									entity: ManualEditEntitiesEnum.Reference,
									copyComposition: "true",
								}),
						},
					]}
				/>
			)}

			<PageDownloadAndMoreActionsMenu
				area="portfolio"
				downloadActions={
					status &&
					!(["REVIEW", "CALCULATING", "DRAFT", "ERROR", "RETRIEVING_DATA"] satisfies InvestmentStatus[]).some(
						(x) => x === status,
					)
						? [
								...(uuid
									? [
											{
												icon: "xls" as const,
												onClickAsync: async () => {
													const config = buildCompositionExportConfig("REFERENCE", {
														investmentUuid: uuid,
														snapshot: "CURRENT",
														colStateKey: persistedColStateKeys["tableV3-reference-composition-read"],
													});
													await runWithErrorReporting(
														async () => {
															const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
															await downloadContentDisposition(file);
														},
														{
															area: "portfolio",
															attemptedOperation: {
																message: "export reference composition",
																payload: JSON.stringify(config),
															},
														},
													).catch(noop);
												},
												label: reference && benchmarkLabeled ? "Benchmark composition" : "Reference composition",
												"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(composition)",
											},
										]
									: []),

								...(uuid && reference
									? [
											{
												icon: "xls" as const,
												onClickAsync: async () => {
													const response = await integrationReferenceApi.exportReference(uuid, {
														responseType: "blob",
													});
													downloadContentDisposition(response);
												},
												label: reference && benchmarkLabeled ? "Benchmark template" : "Reference template",
												"data-qualifier": "PortfolioDetails/Reference/DropdownMenu/DropdownItem(ReferenceTemplate)",
											},
										]
									: []),
							]
						: []
				}
				moreActions={[
					...(aclByArea.portfolio.canDelete(user.id, richAcl?.acl ?? [])
						? [
								{
									icon: "Delete" as const,
									"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(Delete)",
									onClick: unpromisify(entityManagementActions.deleteAsync ?? noop),
									disabled: !entityManagementActions.deleteAsync,
									label: "Delete",
								},
							]
						: []),
					{
						icon: "Content-Copy" as const,
						"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(Duplicate)",
						onClick: unpromisify(entityManagementActions.duplicateAsync ?? noop),
						disabled: !entityManagementActions.duplicateAsync,
						label: "Duplicate",
					},
					...(entityManagementActions.renameAsync
						? [
								{
									"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(Rename)",
									icon: "Edit" as const,
									onClick: unpromisify(entityManagementActions.renameAsync),
									label: "Rename",
								},
							]
						: []),
				]}
			/>
		</div>
	);
}

function ReviewReferenceInner({
	compositionWithCash,
	instruments,
	summary,
}: {
	summary: InvestmentSummary;
} & CompositionInRetrievingDataResponse) {
	const { status } = summary;
	const { t } = useTranslation();

	const columns = columnsDefinition.useInstrumentComposition("read");

	return (
		<Card>
			<Text type="Body/XL/Bold" className="mb-4">
				Composition
			</Text>
			<ReviewCompositionErrors errors={compositionWithCash.uploadErrors} />
			<div className="mb-4" hidden={status === "RETRIEVING_DATA"}>
				<Banner severity="warning" title="Review Before Saving">
					Some instruments require your attention. This may be due to missing information or because they have been
					delisted. Please review the list below and make any necessary updates before saving.
				</Banner>
			</div>
			<div className="mb-4">
				<Banner severity="info">{t("PORTFOLIO_UPLOAD_ALERTS.CALCULATING_UPLOAD")}</Banner>
			</div>
			<AgGridWithModules<Instrument>
				{...agExtra.usePersistColState(persistedColStateKeys["tableV3-reference-composition-read"])}
				columnDefs={columns}
				rowData={instruments}
				getRowId={(x) => x.data.tickerName!}
			/>
		</Card>
	);
}

const severityByStatus: Record<InvestmentStatus, PageHeaderProps["severity"]> = {
	ACCEPTED: "success",
	READY: "success",
	PROPOSAL_READY: "info",
	// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
	// ERROR: "error",
	ERROR: "calculating",
	CALCULATING: "calculating",
	REVIEW: undefined,
	RETRIEVING_DATA: "calculating",
	DRAFT: "info",
	EXECUTING: "calculating",
};
